import React, { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import "./ModalColorPicker.css";

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const rgbToHsl = (r, g, b) => {
	r /= 255; g /= 255; b /= 255;
	const max = Math.max(r, g, b), min = Math.min(r, g, b);
	let h, s, l = (max + min) / 2;
	if (max === min) {
		h = s = 0;
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r: h = (g - b) / d + (g < b ? 6 : 0); break;
			case g: h = (b - r) / d + 2; break;
			case b: h = (r - g) / d + 4; break;
			default: h = 0;
		}
		h /= 6;
	}
	return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const hslToRgb = (h, s, l) => {
	h = (h % 360 + 360) % 360; s = clamp(s, 0, 100) / 100; l = clamp(l, 0, 100) / 100;
	const c = (1 - Math.abs(2 * l - 1)) * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = l - c / 2;
	let r1 = 0, g1 = 0, b1 = 0;
	if (h < 60) { r1 = c; g1 = x; b1 = 0; }
	else if (h < 120) { r1 = x; g1 = c; b1 = 0; }
	else if (h < 180) { r1 = 0; g1 = c; b1 = x; }
	else if (h < 240) { r1 = 0; g1 = x; b1 = c; }
	else if (h < 300) { r1 = x; g1 = 0; b1 = c; }
	else { r1 = c; g1 = 0; b1 = x; }
	return [Math.round((r1 + m) * 255), Math.round((g1 + m) * 255), Math.round((b1 + m) * 255)];
};

export default function ModalColorPicker({
	color,
	onColorChange,
	onClose,
	title = "Color Picker",
}) {
	// Initialize from prop color (RGB array)
	const initialRGB = Array.isArray(color) ? color : [255, 255, 255];
	const initialHSL = rgbToHsl(initialRGB[0], initialRGB[1], initialRGB[2]);

	const [h, setH] = useState(initialHSL.h);
	const [s, setS] = useState(initialHSL.s);
	const [l, setL] = useState(initialHSL.l);

	// Debounced sending
	const lastSentRef = useRef(0);
	const debounceRef = useRef(null);

	const sendColor = useCallback((rgb) => {
		const minInterval = 100; // 10Hz max
		const now = Date.now();
		const elapsed = now - lastSentRef.current;

		const doSend = () => {
			onColorChange?.(rgb);
			lastSentRef.current = Date.now();
		};

		if (elapsed >= minInterval) {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
				debounceRef.current = null;
			}
			doSend();
		} else {
			if (debounceRef.current) clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(doSend, minInterval - elapsed + 10);
		}
	}, [onColorChange]);

	const flushSend = useCallback(() => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}
		lastSentRef.current = 0;
		const rgb = hslToRgb(h, s, l);
		onColorChange?.(rgb);
	}, [h, s, l, onColorChange]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, []);

	// Escape key to close
	useEffect(() => {
		const onKeyDown = (e) => {
			if (e.key === "Escape") {
				e.preventDefault();
				onClose?.();
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [onClose]);

	const handleHueChange = (e) => {
		const hv = clamp(parseInt(e.target.value || "0", 10), 0, 360);
		setH(hv);
		const rgb = hslToRgb(hv, s, l);
		sendColor(rgb);
	};

	const handleSatChange = (e) => {
		const sv = clamp(parseInt(e.target.value || "0", 10), 0, 100);
		setS(sv);
		const rgb = hslToRgb(h, sv, l);
		sendColor(rgb);
	};

	const handleLightChange = (e) => {
		const lv = clamp(parseInt(e.target.value || "0", 10), 0, 100);
		setL(lv);
		const rgb = hslToRgb(h, s, lv);
		sendColor(rgb);
	};

	const currentRgb = hslToRgb(h, s, l);
	const previewStyle = {
		background: `rgb(${currentRgb[0]}, ${currentRgb[1]}, ${currentRgb[2]})`,
		boxShadow: `0 0 30px rgba(${currentRgb[0]}, ${currentRgb[1]}, ${currentRgb[2]}, 0.6)`,
	};

	// Hue gradient for the track
	const hueGradient = "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)";

	// Saturation gradient (grey to full color)
	const fullSatColor = hslToRgb(h, 100, 50);
	const satGradient = `linear-gradient(to right, #808080, rgb(${fullSatColor[0]}, ${fullSatColor[1]}, ${fullSatColor[2]}))`;

	// Lightness gradient (black to color to white)
	const midColor = hslToRgb(h, s, 50);
	const lightGradient = `linear-gradient(to right, #000, rgb(${midColor[0]}, ${midColor[1]}, ${midColor[2]}), #fff)`;

	const modal = (
		<div className="color-modal-overlay" onClick={onClose}>
			<div className="color-modal" onClick={(e) => e.stopPropagation()}>
				<div className="color-modal-header">
					<span className="color-modal-title">{title}</span>
					<button className="color-modal-close" onClick={onClose}>✕</button>
				</div>

				<div className="color-preview" style={previewStyle}></div>

				<div className="color-slider-group">
					<div className="color-slider-row">
						<span className="color-slider-label">Hue</span>
						<span className="color-slider-value">{h}°</span>
					</div>
					<input
						type="range"
						min="0"
						max="360"
						value={h}
						className="color-slider color-slider-hue"
						style={{ background: hueGradient }}
						onChange={handleHueChange}
						onMouseUp={flushSend}
						onTouchEnd={flushSend}
					/>
				</div>

				<div className="color-slider-group">
					<div className="color-slider-row">
						<span className="color-slider-label">Saturation</span>
						<span className="color-slider-value">{s}%</span>
					</div>
					<input
						type="range"
						min="0"
						max="100"
						value={s}
						className="color-slider color-slider-sat"
						style={{ background: satGradient }}
						onChange={handleSatChange}
						onMouseUp={flushSend}
						onTouchEnd={flushSend}
					/>
				</div>

				<div className="color-slider-group">
					<div className="color-slider-row">
						<span className="color-slider-label">Lightness</span>
						<span className="color-slider-value">{l}%</span>
					</div>
					<input
						type="range"
						min="0"
						max="100"
						value={l}
						className="color-slider color-slider-light"
						style={{ background: lightGradient }}
						onChange={handleLightChange}
						onMouseUp={flushSend}
						onTouchEnd={flushSend}
					/>
				</div>

				<button className="color-modal-done" onClick={onClose}>Done</button>
			</div>
		</div>
	);

	return createPortal(modal, document.body);
}
